import {
  Button,
  DialogActionTrigger,
  ButtonGroup,
  DialogTitle,
  Input,
  Box,
  Icon,
  Text,
  VStack,
  HStack,
  Table,
  Switch,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { LuPenLine, LuDatabase } from "react-icons/lu";
import { TableFieldsService, SchemaService } from "../../client";
import type {
  ApiError,
  TableFieldCreate,
  FieldType,
  NumberFieldMetadataCreate,
  StringFieldMetadataCreate,
  DatetimeFieldMetadataCreate,
  SelectionFieldMetadataCreate,
  FileFieldMetadataCreate,
} from "../../client";
import useCustomToast from "../../hooks/useCustomToast";
import { handleError } from "../../utils";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";
import { Checkbox } from "../ui/checkbox";
import TempFieldEditDialog from "./TempFieldEditDialog";

interface AddTableFieldProps {
  tableId: string;
}

interface SchemaField {
  field_name: string;
  description: string;
  field_type: FieldType;
  field_code?: string;
  is_required: boolean;
  metadata?: Record<string, unknown>;
  mapped_name?: string;
  mapped_description?: string;
}

export type SelectedField = SchemaField & {
  selected: boolean;
  number_metadata?: NumberFieldMetadataCreate | null;
  string_metadata?: StringFieldMetadataCreate | null;
  datetime_metadata?: DatetimeFieldMetadataCreate | null;
  selection_metadata?: SelectionFieldMetadataCreate | null;
  file_metadata?: FileFieldMetadataCreate | null;
};

// 중첩된 스키마를 평면화하는 함수
const flattenSchema = (nodes: unknown[]): SchemaField[] => {
  const fields: SchemaField[] = [];

  for (const node of nodes) {
    if (typeof node !== "object" || node === null) continue;

    const nodeObj = node as Record<string, unknown>;
    const name = String(nodeObj.name || "");
    const type = String(nodeObj.type || "string");
    const description = String(nodeObj.description || "");
    const children = Array.isArray(nodeObj.children)
      ? nodeObj.children
      : undefined;

    // 부모 경로를 제거하고 필드명만 사용
    const fieldName = name;

    // 타입 매핑 (DB에서 가져온 스키마는 소문자로 저장되어 있음)
    let fieldType: FieldType = "string"; // 기본값
    switch (type.toLowerCase()) {
      case "string":
        fieldType = "string";
        break;
      case "number":
      case "integer":
      case "float":
        fieldType = "number";
        break;
      case "datetime":
        fieldType = "datetime";
        break;
      case "selection":
        fieldType = "selection";
        break;
      case "file":
        fieldType = "file";
        break;
      case "array":
        fieldType = "selection"; // 배열을 selection으로 처리
        break;
      case "object":
        // 객체인 경우 하위 필드들을 재귀적으로 처리
        if (children) {
          fields.push(...flattenSchema(children));
        }
        continue; // 객체 자체는 필드로 추가하지 않음
      default:
        fieldType = "string";
    }

    fields.push({
      field_name: fieldName,
      description: description || `${fieldName} 필드`,
      field_type: fieldType,
      field_code: name,
      is_required: true, // 기본적으로 필수로 설정
      metadata:
        type.toLowerCase() === "object" && children ? { children } : undefined,
      mapped_name: name,
      mapped_description: description || `${name} 필드`,
    });
  }

  return fields;
};

const AddTableFieldFromSchema = ({ tableId }: AddTableFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: 스키마 선택, 2: 필드 선택, 3: 매핑
  const [selectedSchemaId, setSelectedSchemaId] = useState<string>("");
  const [selectedFields, setSelectedFields] = useState<SelectedField[]>([]);
  const queryClient = useQueryClient();
  const { showSuccessToast } = useCustomToast();
  const [tempEditDialog, setTempEditDialog] = useState<{
    isOpen: boolean;
    field: SelectedField | null;
  }>({ isOpen: false, field: null });

  // 스키마 목록 조회
  const { data: schemas, isLoading: schemasLoading } = useQuery({
    queryKey: ["schemas"],
    queryFn: () => SchemaService.getSchemas(),
  });

  // 선택된 스키마 조회
  const { data: selectedSchema } = useQuery({
    queryKey: ["schema", selectedSchemaId],
    queryFn: () => SchemaService.getSchema({ schemaId: selectedSchemaId }),
    enabled: !!selectedSchemaId,
  });

  // 스키마에서 필드 목록 생성
  const schemaFields = selectedSchema?.schema_content
    ? flattenSchema(selectedSchema.schema_content as unknown[])
    : [];

  // 필드 생성 뮤테이션
  const mutation = useMutation({
    mutationFn: async (fields: TableFieldCreate[]) => {
      const results = [];
      for (const field of fields) {
        const result = await TableFieldsService.createTableField({
          requestBody: field,
          tableId,
        });
        results.push(result);
      }
      return results;
    },
    onSuccess: () => {
      showSuccessToast("테이블 필드가 성공적으로 생성되었습니다.");
      setIsOpen(false);
      setCurrentStep(1);
      setSelectedSchemaId("");
      setSelectedFields([]);
    },
    onError: (err: ApiError) => {
      handleError(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tableFields"] });
    },
  });

  // 스키마 선택 핸들러
  const handleSchemaSelect = (schemaId: string) => {
    setSelectedSchemaId(schemaId);
    setCurrentStep(2);
  };

  // 필드 선택 핸들러
  const handleFieldSelect = (fieldIndex: number, selected: boolean) => {
    if (!schemaFields.length) return;

    const field = schemaFields[fieldIndex];
    setSelectedFields((prev) => {
      const existing = prev.find((f) => f.field_name === field.field_name);
      if (existing) {
        return prev.map((f) =>
          f.field_name === field.field_name ? { ...f, selected } : f
        );
      }
      return [
        ...prev,
        {
          ...field,
          selected,
          mapped_name: field.field_name,
          mapped_description: field.description,
        } as SelectedField,
      ];
    });
  };

  // 필수 여부 변경 핸들러
  const handleRequiredChange = (fieldName: string, isRequired: boolean) => {
    setSelectedFields((prev) => {
      const existing = prev.find((f) => f.field_name === fieldName);
      if (existing) {
        return prev.map((field) =>
          field.field_name === fieldName
            ? { ...field, is_required: isRequired }
            : field
        );
      }

      // 필드가 선택되지 않은 경우에도 추가
      const field = schemaFields.find((f) => f.field_name === fieldName);
      if (field) {
        return [
          ...prev,
          {
            ...field,
            selected: false,
            is_required: isRequired,
            mapped_name: field.field_name,
            mapped_description: field.description,
          } as SelectedField,
        ];
      }

      return prev;
    });
  };

  // 전체 필드 선택/해제
  const handleSelectAll = (selected: boolean) => {
    if (!schemaFields.length) return;

    setSelectedFields((prev) => {
      const updatedFields = schemaFields.map((field) => {
        // 기존에 수정된 데이터가 있는지 확인
        const existingField = prev.find(
          (f) => f.field_name === field.field_name
        );

        if (existingField) {
          // 기존 수정된 데이터를 보존하고 selected 상태만 변경
          return {
            ...existingField,
            selected,
          };
        }
        // 새로운 필드인 경우 기본값으로 생성
        return {
          ...field,
          selected,
          mapped_name: field.field_name,
          mapped_description: field.description,
        } as SelectedField;
      });

      return updatedFields;
    });
  };

  // 필드 매핑 정보 업데이트
  const handleFieldMapping = (
    fieldName: string,
    key: "mapped_name" | "mapped_description",
    value: string
  ) => {
    setSelectedFields((prev) =>
      prev.map((field) =>
        field.field_name === fieldName ? { ...field, [key]: value } : field
      )
    );
  };

  // 필드 생성 실행
  const handleCreateFields = () => {
    const fieldsToCreate = selectedFields
      .filter((field) => field.selected)
      .map((field) => {
        const baseField = {
          field_name: field.mapped_name || field.field_name,
          description: field.mapped_description || field.description,
          field_code: field.field_code,
          field_type: field.field_type,
          is_required: field.is_required,
        };

        // 필드 타입에 따른 메타데이터 설정
        switch (field.field_type) {
          case "number":
            return {
              ...baseField,
              number_metadata: {
                number_type: "natural",
                unit: "",
                min_value: undefined,
                max_value: undefined,
                decimal_round: undefined,
              },
              string_metadata: null,
              datetime_metadata: null,
              selection_metadata: null,
              file_metadata: null,
            } as TableFieldCreate;

          case "string":
            return {
              ...baseField,
              number_metadata: null,
              string_metadata: {
                string_type: "shortText",
              },
              datetime_metadata: null,
              selection_metadata: null,
              file_metadata: null,
            } as TableFieldCreate;

          case "datetime":
            return {
              ...baseField,
              number_metadata: null,
              string_metadata: null,
              datetime_metadata: {
                datetime_type: "datetime",
              },
              selection_metadata: null,
              file_metadata: null,
            } as TableFieldCreate;

          case "selection":
            return {
              ...baseField,
              number_metadata: null,
              string_metadata: null,
              datetime_metadata: null,
              selection_metadata: {
                options: [],
              },
              file_metadata: null,
            } as TableFieldCreate;

          case "file":
            return {
              ...baseField,
              number_metadata: null,
              string_metadata: null,
              datetime_metadata: null,
              selection_metadata: null,
              file_metadata: {
                allowed_extension: "pdf",
                max_file_size_mb: 10,
              },
            } as TableFieldCreate;

          default:
            return {
              ...baseField,
              number_metadata: null,
              string_metadata: {
                string_type: "shortText",
              },
              datetime_metadata: null,
              selection_metadata: null,
              file_metadata: null,
            } as TableFieldCreate;
        }
      });

    mutation.mutate(fieldsToCreate);
  };

  const getFieldTypeLabel = (type: FieldType) => {
    const labels = {
      string: "문자열",
      number: "숫자",
      datetime: "날짜/시간",
      selection: "선택",
      file: "파일",
    };
    return labels[type];
  };

  const handleTempFieldEdit = (field: SelectedField) => {
    setTempEditDialog({ isOpen: true, field });
  };

  const handleTempFieldEditSave = (updated: SelectedField) => {
    setSelectedFields((prev) => {
      const existing = prev.find((f) => f.field_name === updated.field_name);
      if (existing) {
        return prev.map((f) =>
          f.field_name === updated.field_name
            ? { ...f, ...updated, selected: f.selected } // selected 상태 보존
            : f
        );
      }
      // 필드가 selectedFields에 없으면 추가 (selected: false로 추가)
      return [...prev, { ...updated, selected: false }];
    });
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "xl" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button value="add-item" my={4} disabled={!tableId}>
          <LuDatabase fontSize="16px" />
          스키마에서 가져오기
        </Button>
      </DialogTrigger>
      <DialogContent asChild>
        <Box>
          <DialogHeader>
            <DialogTitle>
              {currentStep === 1 && "스키마 선택"}
              {currentStep === 2 && "필드 선택"}
              {currentStep === 3 && "필드 매핑"}
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            {currentStep === 1 && (
              <VStack gap={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  가져올 필드가 포함된 스키마를 선택하세요.
                </Text>

                {schemasLoading ? (
                  <Text>스키마를 불러오는 중...</Text>
                ) : schemas?.data && schemas.data.length > 0 ? (
                  <VStack gap={3} align="stretch">
                    {schemas.data.map((schema) => (
                      <Box
                        key={schema.id}
                        p={4}
                        borderWidth={1}
                        borderColor="gray.200"
                        borderRadius="md"
                        cursor="pointer"
                        _hover={{ borderColor: "blue.300", bg: "blue.50" }}
                        onClick={() => handleSchemaSelect(schema.id)}
                      >
                        <HStack justify="space-between">
                          <VStack align="start" gap={1}>
                            <Text fontWeight="bold">{schema.schema_name}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {schema.description}
                            </Text>
                          </VStack>
                          <Icon as={LuDatabase} color="blue.500" />
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box
                    p={4}
                    bg="blue.50"
                    borderWidth={1}
                    borderColor="blue.200"
                    borderRadius="md"
                  >
                    <Text color="blue.800">사용 가능한 스키마가 없습니다.</Text>
                  </Box>
                )}
              </VStack>
            )}

            {currentStep === 2 && selectedSchema && (
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="bold">
                    스키마: {selectedSchema.schema_name} (
                    {selectedFields.filter((f) => f.selected).length}개 선택)
                  </Text>
                </HStack>

                <Box borderTopWidth={1} borderColor="gray.200" />

                {selectedSchema.schema_content &&
                selectedSchema.schema_content.length > 0 ? (
                  <Table.Root size={{ base: "sm", md: "md" }}>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>
                          <HStack gap={2}>
                            <Checkbox
                              checked={
                                selectedFields.length > 0 &&
                                selectedFields.every((f) => f.selected)
                              }
                              onCheckedChange={(details) =>
                                handleSelectAll(details.checked as boolean)
                              }
                            />
                          </HStack>
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>필드명</Table.ColumnHeader>
                        <Table.ColumnHeader>설명</Table.ColumnHeader>
                        <Table.ColumnHeader>필드타입</Table.ColumnHeader>
                        <Table.ColumnHeader>필수여부</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {schemaFields.map((field, index) => {
                        const selectedField = selectedFields.find(
                          (f) => f.field_name === field.field_name
                        );
                        const isSelected = selectedField?.selected || false;
                        const isRequired =
                          selectedField?.is_required ?? field.is_required;
                        return (
                          <Table.Row key={`${field.field_name}-${index}`}>
                            <Table.Cell>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(details) =>
                                  handleFieldSelect(
                                    index,
                                    details.checked as boolean
                                  )
                                }
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Text fontWeight="medium">
                                {selectedField?.mapped_name || field.field_name}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Text fontSize="sm">
                                {selectedField?.mapped_description ||
                                  field.description}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <HStack>
                                <Icon
                                  as={LuPenLine}
                                  cursor="pointer"
                                  onClick={() =>
                                    handleTempFieldEdit(
                                      selectedField || {
                                        ...field,
                                        selected: false,
                                      }
                                    )
                                  }
                                  _hover={{
                                    color: "blue.500",
                                    transform: "scale(1.1)",
                                    transition: "all 0.2s",
                                  }}
                                />
                                <Text fontSize="sm">
                                  {getFieldTypeLabel(
                                    selectedField?.field_type ||
                                      field.field_type
                                  )}
                                </Text>
                              </HStack>
                            </Table.Cell>
                            <Table.Cell>
                              <HStack gap={2}>
                                <Switch.Root
                                  size="sm"
                                  checked={isRequired}
                                  onCheckedChange={(details) =>
                                    handleRequiredChange(
                                      field.field_name,
                                      details.checked as boolean
                                    )
                                  }
                                >
                                  <Switch.HiddenInput />
                                  <Switch.Control />
                                </Switch.Root>
                                <Text fontSize="sm" color="gray.600">
                                  {isRequired ? "필수" : "선택"}
                                </Text>
                              </HStack>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Box
                    p={4}
                    bg="yellow.50"
                    borderWidth={1}
                    borderColor="yellow.200"
                    borderRadius="md"
                  >
                    <Text color="yellow.800">
                      이 스키마에는 필드가 없습니다.
                    </Text>
                  </Box>
                )}
              </VStack>
            )}

            {currentStep === 3 && (
              <VStack gap={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  선택된 필드들의 정보를 확인하고 필요시 수정하세요.
                </Text>

                {selectedFields
                  .filter((f) => f.selected)
                  .map((field, index) => (
                    <Box
                      key={`${field.field_name}-${index}`}
                      p={4}
                      borderWidth={1}
                      borderColor="gray.200"
                      borderRadius="md"
                    >
                      <VStack gap={3} align="stretch">
                        <Field required={field.is_required} label="필드명">
                          <Input
                            value={field.mapped_name || field.field_name}
                            onChange={(e) =>
                              handleFieldMapping(
                                field.field_name,
                                "mapped_name",
                                e.target.value
                              )
                            }
                            placeholder="필드명을 입력하세요"
                          />
                        </Field>

                        <Field label="설명">
                          <Input
                            value={
                              field.mapped_description || field.description
                            }
                            onChange={(e) =>
                              handleFieldMapping(
                                field.field_name,
                                "mapped_description",
                                e.target.value
                              )
                            }
                            placeholder="설명을 입력하세요"
                          />
                        </Field>
                      </VStack>
                    </Box>
                  ))}
              </VStack>
            )}
          </DialogBody>

          <DialogFooter gap={3}>
            <ButtonGroup>
              {currentStep > 1 && (
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  onClick={() => {
                    if (currentStep === 2) {
                      setCurrentStep(1);
                      setSelectedSchemaId("");
                      setSelectedFields([]);
                    } else if (currentStep === 3) {
                      setCurrentStep(2);
                    }
                  }}
                >
                  이전
                </Button>
              )}

              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={mutation.isPending}
                >
                  취소
                </Button>
              </DialogActionTrigger>

              {currentStep === 2 && (
                <Button
                  variant="solid"
                  onClick={() => setCurrentStep(3)}
                  disabled={
                    selectedFields.filter((f) => f.selected).length === 0
                  }
                >
                  다음
                </Button>
              )}

              {currentStep === 3 && (
                <Button
                  variant="solid"
                  onClick={handleCreateFields}
                  loading={mutation.isPending}
                  disabled={
                    selectedFields.filter((f) => f.selected).length === 0
                  }
                >
                  필드 생성 ({selectedFields.filter((f) => f.selected).length}
                  개)
                </Button>
              )}
            </ButtonGroup>
          </DialogFooter>

          <DialogCloseTrigger />
        </Box>
      </DialogContent>
      {tempEditDialog.field && (
        <TempFieldEditDialog
          isOpen={tempEditDialog.isOpen}
          onClose={() => setTempEditDialog({ isOpen: false, field: null })}
          field={tempEditDialog.field}
          onSave={handleTempFieldEditSave}
        />
      )}
    </DialogRoot>
  );
};

export default AddTableFieldFromSchema;
